import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';
import { Card, Col, Form, Row, Table } from 'react-bootstrap';
import Select from 'react-select';
import { BASE_URL } from "../../services/Api";
import { authHeader } from "../../services/auth-header";
import debounce from 'lodash/debounce';

function OpeningStockCreate() {
    const navigate = useNavigate();

    const [warehouseOptions, setWarehouseOptions] = useState([]);
    const [selectedWarehouse, setSelectedWarehouse] = useState(null);

    const [selectedProduct, setSelectedProduct] = useState(null);
    const [productOptions, setProductOptions] = useState([]);
    const [productSearch, setProductSearch] = useState(encodeURIComponent(" "));
    const [row, setRow] = useState(null);
    const [openingDate, setOpeningDate] = useState('');

    // 🔹 Fetch All Warehouses
    const getWarehouseData = async () => {
        try {
            const response = await axios.get(`${BASE_URL}warehouse/findAllWarehouse`, {
                headers: authHeader()
            });
            const data = response.data || [];
            const options = data.map(w => ({
                value: w.id,
                label: w.name || w.warehousename
            }));
            setWarehouseOptions(options);
        } catch (error) {
            console.error('Error fetching warehouses:', error);
        }
    };

    // 🔹 Product Search Handling
    const handleProductSearchChange = debounce((inputValue) => {
        setProductSearch(encodeURIComponent(inputValue || " "));
    }, 300);

    const getProductData = async () => {
        try {
            const response = await axios.get(
                `${BASE_URL}product/page/ptype/0/10/desc/id/2/Finished,Raw Material, Semi Finished,Services/${productSearch}`,
                { headers: authHeader() }
            );

            const products = response.data?.Index || response.data?.data || [];
            const mapped = products.map(product => ({
                value: product.id,
                label: `${product.description} - ${product.serialnumber} - ${product.partno}`,
                description: product.description,
            }));
            setProductOptions(mapped);
        } catch (error) {
            console.error('Error fetching products:', error);
        }
    };

    useEffect(() => {
        getWarehouseData();
    }, []);

    useEffect(() => {
        getProductData();
    }, [productSearch]);

    // 🔹 Product Select (only one)
    const handleProductChange = (selectedOption) => {
        setSelectedProduct(selectedOption);

        if (!selectedOption) {
            setRow(null);
            return;
        }

        const newRow = {
            description: selectedOption.description,
            value: selectedOption.value,
            label: selectedOption.label,
            quantity: '',
        };

        setRow(newRow); // only one product row
    };

    const handleQuantityChange = (value) => {
        setRow(prev => ({ ...prev, quantity: value }));
    };

    const removeRow = () => {
        setRow(null);
        setSelectedProduct(null);
    };

    // 🔹 Submit Handler
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedWarehouse) {
            Swal.fire({
                icon: 'warning',
                title: 'Warehouse Required',
                text: 'Please select a warehouse.',
            });
            return;
        }

        if (!openingDate) {
            Swal.fire({
                icon: 'warning',
                title: 'Opening Date Required',
                text: 'Please select opening date.',
            });
            return;
        }

        if (!row) {
            Swal.fire({
                icon: 'warning',
                title: 'Product Required',
                text: 'Please select a product.',
            });
            return;
        }

        if (!row.quantity || parseInt(row.quantity) <= 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Quantity Required',
                text: 'Please enter a valid quantity.',
            });
            return;
        }

        const payload = {
            product: { id: row.value },
            warehouse: { id: selectedWarehouse.value },
            quantity: parseInt(row.quantity),
            openingdate: openingDate
        };

        try {
            await axios.post(`${BASE_URL}openingstock/save`, payload, {
                headers: authHeader()
            });
            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Opening stock added successfully!'
            });
            navigate("/opening-stock/1");
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data || 'Failed to add opening stock.'
            });
        }
    };

    return (
        <div className="container mt-4">
            <Card className="shadow-sm">
                <Card.Header>
                    <h5 className="mb-0">Create Opening Stock</h5>
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={handleSubmit}>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Warehouse *</Form.Label>
                                    <Select
                                        value={selectedWarehouse}
                                        onChange={setSelectedWarehouse}
                                        options={warehouseOptions}
                                        placeholder="Select warehouse"
                                        isClearable
                                        required
                                        menuPortalTarget={document.body}
                                        styles={{
                                            menuPortal: base => ({ ...base, zIndex: 9999 })
                                        }}
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Opening Date *</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={openingDate}
                                        onChange={(e) => setOpeningDate(e.target.value)}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group controlId="productSelect" className="mb-3">
                            <Form.Label>Select Product *</Form.Label>
                            <Select
                                value={selectedProduct}
                                onInputChange={handleProductSearchChange}
                                onChange={handleProductChange}
                                options={productOptions}
                                placeholder="Search or select product"
                                isClearable
                                menuPortalTarget={document.body}
                                styles={{
                                    menuPortal: base => ({ ...base, zIndex: 9999 })
                                }}
                            />
                        </Form.Group>

                        {row && (
                            <div className="table-responsive mt-3">
                                <Table striped bordered hover>
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Product Description</th>
                                            <th>Quantity *</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>1</td>
                                            <td>{row.description}</td>
                                            <td>
                                                <Form.Control
                                                    type="number"
                                                    value={row.quantity}
                                                    onChange={(e) => handleQuantityChange(e.target.value)}
                                                    required
                                                    min="1"
                                                />
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="btn btn-danger btn-sm"
                                                    onClick={removeRow}
                                                >
                                                    Remove
                                                </button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </div>
                        )}

                        <div className="text-end">
                            <button className="btn btn-primary mt-3" type="submit">SUBMIT</button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </div>
    );
}

export default OpeningStockCreate;

